
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EcommercePlatform, EcommerceSettings } from '@/components/admin/campaigns/types';
import EcommerceConnectionTab from './tabs/EcommerceConnectionTab';
import EcommerceWidgetTab from './tabs/EcommerceWidgetTab';
import EcommerceRewardsTab from './tabs/EcommerceRewardsTab';
import EcommerceTrackingTab from './tabs/EcommerceTrackingTab';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface EcommerceIntegrationSetupProps {
  platform: EcommercePlatform;
}

const EcommerceIntegrationSetup: React.FC<EcommerceIntegrationSetupProps> = ({
  platform,
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('connection');
  const [settings, setSettings] = useState<EcommerceSettings>({
    platform,
    storeUrl: '',
    apiKey: '',
    apiSecret: '',
    isConnected: false,
    widgetPlacement: 'product_page',
    widgetType: 'popup',
    rewardType: 'fixed',
    rewardValue: 10,
    rewardCurrency: 'USD',
    postPurchaseEnabled: true,
    trackingParameter: 'ref',
    autoRewardEnabled: true,
  });

  const updateSettings = (newSettings: Partial<EcommerceSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const handleSaveSettings = () => {
    toast({
      title: "Settings Saved",
      description: `Your ${getPlatformName(platform)} integration settings have been saved.`,
    });
  };

  const getPlatformName = (platform: EcommercePlatform): string => {
    const platformMap: Record<EcommercePlatform, string> = {
      shopify: 'Shopify',
      woocommerce: 'WooCommerce',
      bigcommerce: 'BigCommerce',
      volusion: 'Volusion',
      wordpress: 'WordPress',
      squarespace: 'Squarespace',
      magento: 'Magento',
      shift4shop: 'Shift4Shop'
    };
    
    return platformMap[platform] || platform;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{getPlatformName(platform)} Integration Setup</CardTitle>
        <CardDescription>
          Configure your {getPlatformName(platform)} integration to enable referral campaigns
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="connection">Connection</TabsTrigger>
            <TabsTrigger value="widget">Widget Setup</TabsTrigger>
            <TabsTrigger value="rewards">Rewards</TabsTrigger>
            <TabsTrigger value="tracking">Tracking</TabsTrigger>
          </TabsList>

          <TabsContent value="connection" className="pt-4">
            <EcommerceConnectionTab 
              platform={platform}
              settings={settings}
              updateSettings={updateSettings}
            />
          </TabsContent>

          <TabsContent value="widget" className="pt-4">
            <EcommerceWidgetTab 
              settings={settings}
              updateSettings={updateSettings}
            />
          </TabsContent>

          <TabsContent value="rewards" className="pt-4">
            <EcommerceRewardsTab 
              settings={settings}
              updateSettings={updateSettings}
            />
          </TabsContent>

          <TabsContent value="tracking" className="pt-4">
            <EcommerceTrackingTab 
              settings={settings}
              updateSettings={updateSettings}
            />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button onClick={handleSaveSettings}>
            Save Integration Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EcommerceIntegrationSetup;
